/**
 * Work Orders Page
 * Vista para gestionar órdenes de trabajo de tratamientos de control de plagas
 */

import { useState, useEffect } from "react";
import { Icon, WaterSampleFormModal } from "@/components/ui";
import { workOrdersService } from "@/services/workOrdersService";
import {
  waterSamplesService,
  type WaterSampleForm,
} from "@/services/waterSamplesService";
import { notifyAction } from "@/utils";
import { useAuth } from "@/contexts/AuthContext";
import type { APIWorkOrderDetail } from "@/types";
import styles from "./WorkOrdersPage.module.css";

interface Client {
  id: string;
  name: string;
  address: string;
  phone: string;
}

interface Technician {
  id: string;
  name: string;
  initials: string;
}

interface Pest {
  id: string;
  name: string;
  icon?: string;
}

interface Material {
  id: string;
  name: string;
  quantity: number;
  used?: number;
}

interface ActionStep {
  id: string;
  order: number;
  description: string;
  completed: boolean;
  notes?: string;
}

interface WorkOrder {
  id: string;
  orderNumber: string;
  client: Client;
  technician: Technician | null;
  pest: Pest;
  status: "pending" | "assigned" | "inProgress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  scheduledDate: string;
  completedDate?: string;
  startTime?: string;
  endTime?: string;
  totalMinutes?: number;
  actionPlan: ActionStep[];
  materials: Material[];
  observations?: string;
  clientSignature?: string;
  technicianSignature?: string;
  photos?: string[];
  createdAt: string;
}

const mapWorkOrderPhotos = (photos: APIWorkOrderDetail["photos"]): string[] => {
  return (photos ?? [])
    .map((photo) => {
      if (typeof photo === "string") return photo;
      return photo.photo ?? photo.image ?? photo.file ?? photo.url ?? null;
    })
    .filter((photo): photo is string => Boolean(photo));
};

type WorkOrderMaterialSource =
  | (APIWorkOrderDetail["materials"][number] & {
      used?: number;
      material_name?: string;
    })
  | {
      material: { id: number; name: string };
      quantity: number;
    };

const getMaterialName = (material: WorkOrderMaterialSource): string => {
  if (
    "material_detail" in material &&
    typeof material.material_detail?.name === "string"
  ) {
    return material.material_detail.name;
  }

  if (
    "material" in material &&
    typeof material.material === "object" &&
    material.material !== null &&
    typeof material.material.name === "string"
  ) {
    return material.material.name;
  }

  if ("material_name" in material && typeof material.material_name === "string") {
    return material.material_name;
  }

  if (
    "material" in material &&
    (typeof material.material === "number" ||
      typeof material.material === "string")
  ) {
    return `Material #${material.material}`;
  }

  return "";
};

/** Mapea APIWorkOrderDetail al tipo local WorkOrder */
const mapDetailToWorkOrder = (
  wo: APIWorkOrderDetail,
  fallback?: WorkOrder,
): WorkOrder => ({
  id: String(wo.id),
  orderNumber: wo.order_number,
  client: {
    id:
      typeof wo.client === "object" && wo.client !== null
        ? String(wo.client.id)
        : (fallback?.client.id ?? String(wo.client ?? "")),
    name:
      typeof wo.client === "object" && wo.client !== null
        ? wo.client.name
        : (fallback?.client.name ?? "Cliente"),
    address:
      typeof wo.client === "object" && wo.client !== null
        ? wo.client.address
        : (fallback?.client.address ?? ""),
    phone:
      typeof wo.client === "object" && wo.client !== null
        ? (wo.client.phone_number ?? "")
        : (fallback?.client.phone ?? ""),
  },
  technician:
    wo.assigned_technician && typeof wo.assigned_technician === "object"
      ? {
          id: String(wo.assigned_technician.id),
          name: wo.assigned_technician.name,
          initials: wo.assigned_technician.name
            .split(/[.@\s]/)
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0].toUpperCase())
            .join(""),
        }
      : (fallback?.technician ?? null),
  pest: wo.plague_detail
    ? {
        id: String(wo.plague_detail.id),
        name: wo.plague_detail.name,
        icon: wo.plague_detail.emoji ?? "",
      }
    : wo.plague && typeof wo.plague === "object"
      ? {
          id: String(wo.plague.id),
          name: wo.plague.name,
          icon: wo.plague.emoji ?? "",
        }
      : (fallback?.pest ?? { id: "0", name: "Sin plaga asignada", icon: "" }),
  status: (wo.status === "in_progress"
    ? "inProgress"
    : wo.status) as WorkOrder["status"],
  priority: (["low", "medium", "high", "urgent"].includes(wo.urgency ?? "")
    ? wo.urgency
    : "medium") as WorkOrder["priority"],
  scheduledDate: wo.scheduled_date ?? new Date().toISOString().split("T")[0],
  completedDate: wo.completed_at ?? fallback?.completedDate,
  startTime: wo.started_at ?? fallback?.startTime,
  endTime: wo.completed_at ?? fallback?.endTime,
  actionPlan: ((
    (wo.treatments ?? []) as Array<
      APIWorkOrderDetail["treatments"][number] & {
        name?: string;
        treatment_detail?: { name?: string };
        description?: string | null;
        order?: number;
      }
    >
  ).length > 0
    ? ((wo.treatments ?? []) as Array<
        APIWorkOrderDetail["treatments"][number] & {
          name?: string;
          treatment_detail?: { name?: string };
          description?: string | null;
          order?: number;
        }
      >)
    : ((wo.plague_detail?.treatments ?? []) as Array<{
        id: number;
        name: string;
        description?: string | null;
      }>)
  ).map((t, i) => ({
    id: String(t.id ?? i),
    order: "order" in t && t.order ? t.order : i + 1,
    description:
      ("treatment_name" in t ? t.treatment_name : undefined) ??
      t.name ??
      ("treatment_detail" in t ? t.treatment_detail?.name : undefined) ??
      t.description ??
      "",
    completed: ("completed" in t ? t.completed : false) ?? false,
  })),
  materials: ((
    (wo.materials ?? []) as WorkOrderMaterialSource[]
  ).length > 0
    ? ((wo.materials ?? []) as WorkOrderMaterialSource[])
    : ((wo.plague_detail?.materials_detail ?? []) as WorkOrderMaterialSource[])
  ).map((m) => ({
    id:
      "material" in m && typeof m.material === "object"
        ? String(m.material.id)
        : String("id" in m ? m.id : "material" in m ? m.material : ""),
    name: getMaterialName(m),
    quantity: m.quantity,
    used: "used" in m ? m.used : undefined,
  })),
  observations: wo.notes ?? fallback?.observations,
  clientSignature: wo.signature ?? fallback?.clientSignature,
  photos: mapWorkOrderPhotos(wo.photos),
  createdAt: wo.created_at,
});

export function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [isWaterSampleModalOpen, setIsWaterSampleModalOpen] = useState(false);
  const [existingWaterSampleForm, setExistingWaterSampleForm] =
    useState<WaterSampleForm | null>(null);
  const [isLoadingWaterSample, setIsLoadingWaterSample] = useState(false);
  const { user: currentUser } = useAuth();
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());

  // Actualizar tiempo cada minuto para órdenes en progreso
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(timer);
  }, []);

  // Si la orden seleccionada es una Legionella completada, intentamos
  // localizar un acta de muestras de agua ya existente para esa orden.
  useEffect(() => {
    setExistingWaterSampleForm(null);

    if (!selectedOrder) return;
    if (selectedOrder.status !== "completed") return;
    if (!/^legionel/i.test(selectedOrder.pest.name)) return;

    const clientId = Number(selectedOrder.client.id);
    const orderNumber = selectedOrder.orderNumber;
    if (!clientId || !orderNumber) return;

    let cancelled = false;
    setIsLoadingWaterSample(true);
    waterSamplesService
      .findByWorkOrder({ clientId, workOrderNumber: orderNumber })
      .then((form) => {
        if (!cancelled) setExistingWaterSampleForm(form);
      })
      .catch(() => {
        if (!cancelled) setExistingWaterSampleForm(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingWaterSample(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedOrder?.id,
    selectedOrder?.status,
    selectedOrder?.pest.name,
    selectedOrder?.client.id,
    selectedOrder?.orderNumber,
    selectedOrder,
  ]);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const res = await workOrdersService.list({ page_size: 100 });
        const mapped: WorkOrder[] = res.results.map(
          (wo): WorkOrder => ({
            id: String(wo.id),
            orderNumber: wo.order_number,
            client: {
              id: String(wo.client.id),
              name: wo.client.name,
              address: wo.client.address,
              phone: wo.client.phone_number ?? "",
            },
            technician: wo.assigned_technician
              ? {
                  id: String(wo.assigned_technician.id),
                  name: wo.assigned_technician.name,
                  initials: wo.assigned_technician.name
                    .split(/[.@\s]/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s[0].toUpperCase())
                    .join(""),
                }
              : null,
            pest: wo.plague
              ? {
                  id: String(wo.plague.id),
                  name: wo.plague.name,
                  icon: wo.plague.emoji ?? "",
                }
              : { id: "0", name: "Sin plaga asignada", icon: "" },
            status: (wo.status === "in_progress"
              ? "inProgress"
              : wo.status) as WorkOrder["status"],
            priority: (["low", "medium", "high", "urgent"].includes(
              wo.urgency ?? "",
            )
              ? wo.urgency
              : "medium") as WorkOrder["priority"],
            scheduledDate:
              wo.scheduled_date ?? new Date().toISOString().split("T")[0],
            completedDate: wo.completed_at ?? undefined,
            startTime: wo.started_at ?? undefined,
            endTime: wo.completed_at ?? undefined,
            actionPlan: [],
            materials: [],
            createdAt: new Date().toISOString(),
          }),
        );
        setWorkOrders(mapped);
      } catch (err) {
        setApiError("Error al cargar las órdenes de trabajo");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const getStatusLabel = (status: WorkOrder["status"]): string => {
    const labels = {
      pending: "Pendiente",
      assigned: "Asignada",
      inProgress: "En Proceso",
      completed: "Completada",
      cancelled: "Cancelada",
    };
    return labels[status];
  };

  const getStatusColor = (status: WorkOrder["status"]): string => {
    const colors = {
      pending: styles.statusPending,
      assigned: styles.statusAssigned,
      inProgress: styles.statusInProgress,
      completed: styles.statusCompleted,
      cancelled: styles.statusCancelled,
    };
    return colors[status];
  };

  const getPriorityLabel = (priority: WorkOrder["priority"]): string => {
    const labels = {
      low: "Baja",
      medium: "Media",
      high: "Alta",
      urgent: "Urgente",
    };
    return labels[priority];
  };

  const getPriorityColor = (priority: WorkOrder["priority"]): string => {
    const colors = {
      low: styles.priorityLow,
      medium: styles.priorityMedium,
      high: styles.priorityHigh,
      urgent: styles.priorityUrgent,
    };
    return colors[priority];
  };

  const getFilteredOrders = () => {
    return workOrders.filter((order) => {
      const matchesStatus =
        filterStatus === "all" || order.status === filterStatus;
      const matchesPriority =
        filterPriority === "all" || order.priority === filterPriority;
      const matchesSearch =
        searchTerm === "" ||
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.pest.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesStatus && matchesPriority && matchesSearch;
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const calculateElapsedTime = (
    order: WorkOrder,
  ): { hours: number; minutes: number } | null => {
    if (!order.startTime) return null;

    const startTime = new Date(order.startTime).getTime();
    if (!Number.isFinite(startTime)) return null;

    const endTime = order.endTime
      ? new Date(order.endTime).getTime()
      : currentTime.getTime();
    if (!Number.isFinite(endTime)) return null;

    const elapsedMinutes = Math.max(
      0,
      Math.floor((endTime - startTime) / 60000),
    );

    return {
      hours: Math.floor(elapsedMinutes / 60),
      minutes: elapsedMinutes % 60,
    };
  };

  const handleCancelOrder = (order: WorkOrder) => {
    notifyAction({
      title: "Cancelar orden de trabajo",
      description: `Se cancelará la orden ${order.orderNumber}.`,
      buttonTitle: "Cancelar orden",
      onClick: () => {
        void (async () => {
          try {
            await workOrdersService.patch(Number(order.id), {
              status: "cancelled",
            });
            setWorkOrders((prev) =>
              prev.map((o) =>
                o.id === order.id ? { ...o, status: "cancelled" as const } : o,
              ),
            );
          } catch (err) {
            console.error("Error cancelando orden:", err);
          }
        })();
      },
    });
  };

  const filteredOrders = getFilteredOrders();

  if (isLoading)
    return (
      <div className={styles.container}>
        <p style={{ padding: "2rem" }}>Cargando órdenes de trabajo...</p>
      </div>
    );
  if (apiError)
    return (
      <div className={styles.container}>
        <p style={{ padding: "2rem", color: "red" }}>{apiError}</p>
      </div>
    );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <Icon name="clipboard" size={32} />
            <div>
              <h1>Órdenes de Trabajo</h1>
              <p>Gestión y seguimiento de tratamientos de control de plagas</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <Icon name="search" size={18} />
            <input
              type="text"
              placeholder="Buscar por número, cliente o plaga..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="assigned">Asignadas</option>
            <option value="inProgress">En Proceso</option>
            <option value="completed">Completadas</option>
            <option value="cancelled">Canceladas</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">Todas las prioridades</option>
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Orders List */}
        <div className={styles.ordersList}>
          {filteredOrders.length === 0 ? (
            <div className={styles.emptyState}>
              <Icon name="clipboard" size={64} />
              <p>No se encontraron órdenes de trabajo</p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                className={`${styles.orderCard} ${
                  selectedOrder?.id === order.id ? styles.selected : ""
                }`}
                onClick={() => {
                  setSelectedOrder(order);
                  setIsLoadingDetail(true);
                  workOrdersService
                    .getById(Number(order.id))
                    .then((detail) =>
                      setSelectedOrder(mapDetailToWorkOrder(detail, order)),
                    )
                    .catch((err) =>
                      console.error("Error loading order detail:", err),
                    )
                    .finally(() => setIsLoadingDetail(false));
                }}
              >
                <div className={styles.orderCardHeader}>
                  <div className={styles.orderNumber}>
                    <span className={styles.pestIcon}>{order.pest.icon}</span>
                    <div>
                      <strong>{order.orderNumber}</strong>
                      <span className={styles.pestName}>{order.pest.name}</span>
                    </div>
                  </div>
                  <div className={styles.badges}>
                    <span
                      className={`${styles.statusBadge} ${getStatusColor(order.status)}`}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                    <span
                      className={`${styles.priorityBadge} ${getPriorityColor(order.priority)}`}
                    >
                      {getPriorityLabel(order.priority)}
                    </span>
                  </div>
                </div>

                <div className={styles.orderCardBody}>
                  <div className={styles.orderInfo}>
                    <Icon name="user" size={16} />
                    <span>{order.client.name}</span>
                  </div>
                  <div className={styles.orderInfo}>
                    <Icon name="mapPin" size={16} />
                    <span>{order.client.address}</span>
                  </div>
                  <div className={styles.orderInfo}>
                    <Icon name="calendar" size={16} />
                    <span>Programada: {formatDate(order.scheduledDate)}</span>
                  </div>
                  {order.technician && (
                    <div className={styles.orderInfo}>
                      <Icon name="users" size={16} />
                      <span>Técnico: {order.technician.name}</span>
                    </div>
                  )}
                  {(() => {
                    const elapsed = calculateElapsedTime(order);
                    if (!elapsed) return null;

                    return (
                      <div className={styles.orderElapsed}>
                        <Icon name="clock" size={16} />
                        <span>
                          Tiempo trabajado:{" "}
                          {String(elapsed.hours).padStart(2, "0")}:
                          {String(elapsed.minutes).padStart(2, "0")}
                        </span>
                        {order.status === "inProgress" && !order.endTime && (
                          <span className={styles.orderElapsedLiveDot}>●</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Order Details */}
        {selectedOrder && (
          <div className={styles.orderDetails}>
            {isLoadingDetail && (
              <div
                style={{ padding: "1rem", fontSize: "13px", color: "#6b7280" }}
              >
                Cargando detalles...
              </div>
            )}
            <div className={styles.detailsHeader}>
              <div className={styles.detailsHeaderRow}>
                <div className={styles.detailsTitle}>
                  <span className={styles.detailsPestIcon}>
                    {selectedOrder.pest.icon}
                  </span>
                  <div>
                    <h2>{selectedOrder.orderNumber}</h2>
                    <p>{selectedOrder.pest.name}</p>
                  </div>
                </div>
                <div className={styles.detailsActions}>
                  {/^legionel/i.test(selectedOrder.pest.name) &&
                    selectedOrder.status === "completed" && (
                      <button
                        className={styles.newButton}
                        onClick={() => setIsWaterSampleModalOpen(true)}
                        disabled={isLoadingWaterSample}
                        title={
                          existingWaterSampleForm
                            ? "Ver acta de muestras de agua"
                            : "Crear acta de muestras de agua"
                        }
                      >
                        <Icon name="document" size={16} />
                        {isLoadingWaterSample
                          ? "Cargando acta..."
                          : existingWaterSampleForm
                            ? "Ver acta de muestra"
                            : "Acta muestras de agua"}
                      </button>
                    )}
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleCancelOrder(selectedOrder)}
                    title="Cancelar"
                  >
                    <Icon name="xCircle" size={18} />
                  </button>
                </div>
              </div>
              <div className={styles.detailsBadges}>
                <span
                  className={`${styles.statusBadge} ${getStatusColor(selectedOrder.status)}`}
                >
                  {getStatusLabel(selectedOrder.status)}
                </span>
                <span
                  className={`${styles.priorityBadge} ${getPriorityColor(selectedOrder.priority)}`}
                >
                  {getPriorityLabel(selectedOrder.priority)}
                </span>
              </div>
            </div>

            <div className={styles.detailsContent}>
              {/* Client Info */}
              <section className={styles.section}>
                <h3>
                  <Icon name="user" size={20} />
                  Información del Cliente
                </h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <label>Cliente</label>
                    <span>{selectedOrder.client.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Dirección</label>
                    <span>{selectedOrder.client.address}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Teléfono</label>
                    <span>{selectedOrder.client.phone}</span>
                  </div>
                </div>
              </section>

              {/* Schedule Info */}
              <section className={styles.section}>
                <h3>
                  <Icon name="calendar" size={20} />
                  Programación y Tiempo
                </h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <label>Fecha Programada</label>
                    <span>{formatDate(selectedOrder.scheduledDate)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Técnico Asignado</label>
                    <span>
                      {selectedOrder.technician?.name || "Sin asignar"}
                    </span>
                  </div>
                  {selectedOrder.completedDate && (
                    <div className={styles.infoItem}>
                      <label>Fecha Completada</label>
                      <span>{formatDate(selectedOrder.completedDate)}</span>
                    </div>
                  )}
                  {selectedOrder.startTime && (
                    <div className={styles.infoItem}>
                      <label>Inicio del Trabajo</label>
                      <span>
                        {new Date(selectedOrder.startTime).toLocaleTimeString(
                          "es-ES",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  )}
                  {selectedOrder.endTime && (
                    <div className={styles.infoItem}>
                      <label>Fin del Trabajo</label>
                      <span>
                        {new Date(selectedOrder.endTime).toLocaleTimeString(
                          "es-ES",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const elapsed = calculateElapsedTime(selectedOrder);
                    if (!elapsed) return null;

                    return (
                      <div className={styles.infoItem}>
                        <label>
                          {selectedOrder.endTime
                            ? "Tiempo Total"
                            : "Tiempo Transcurrido"}
                        </label>
                        <span
                          className={
                            selectedOrder.endTime
                              ? styles.timeWorked
                              : styles.timeRunning
                          }
                        >
                          {String(elapsed.hours).padStart(2, "0")}:
                          {String(elapsed.minutes).padStart(2, "0")}
                          {!selectedOrder.endTime && (
                            <span className={styles.liveDot}>●</span>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                  {selectedOrder.status === "inProgress" &&
                    !selectedOrder.endTime &&
                    selectedOrder.startTime && (
                      <div className={styles.infoItem}>
                        <label>Estado Actual</label>
                        <span className={styles.workingNow}>
                          <Icon name="clock" size={16} />
                          Trabajando ahora
                        </span>
                      </div>
                    )}
                </div>
              </section>

              {/* Action Plan */}
              <section className={styles.section}>
                <h3>
                  <Icon name="list" size={20} />
                  Plan de Actuación
                </h3>
                <div className={styles.actionPlan}>
                  {selectedOrder.actionPlan.length === 0 ? (
                    <p className={styles.emptySection}>
                      {isLoadingDetail
                        ? "Cargando plan de actuación..."
                        : "No hay pasos de actuación registrados para esta orden."}
                    </p>
                  ) : (
                    selectedOrder.actionPlan.map((step) => (
                      <div
                        key={step.id}
                        className={`${styles.actionStep} ${
                          step.completed ? styles.completed : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={step.completed}
                          disabled
                          className={styles.stepCheckbox}
                        />
                        <div className={styles.stepNumber}>{step.order}</div>
                        <div className={styles.stepContent}>
                          <p>{step.description}</p>
                          {step.notes && (
                            <span className={styles.stepNotes}>
                              <Icon name="info" size={14} />
                              {step.notes}
                            </span>
                          )}
                        </div>
                        {step.completed && <Icon name="check" size={20} />}
                      </div>
                    ))
                  )}
                </div>
              </section>

              {/* Materials */}
              <section className={styles.section}>
                <h3>
                  <Icon name="package" size={20} />
                  Materiales
                </h3>
                <div className={styles.materialsList}>
                  {selectedOrder.materials.length === 0 ? (
                    <p className={styles.emptySection}>
                      {isLoadingDetail
                        ? "Cargando materiales..."
                        : "No hay materiales registrados para esta orden."}
                    </p>
                  ) : (
                    selectedOrder.materials.map((material) => (
                      <div key={material.id} className={styles.materialItem}>
                        <span className={styles.materialName}>
                          {material.name}
                        </span>
                        <span className={styles.materialQuantity}>
                          {material.used !== undefined
                            ? `${material.used} / ${material.quantity}`
                            : material.quantity}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section className={styles.section}>
                <h3>
                  <Icon name="image" size={20} />
                  Evidencias
                </h3>

                <div className={styles.evidenceBlock}>
                  <label>Fotos</label>
                  {selectedOrder.photos && selectedOrder.photos.length > 0 ? (
                    <div className={styles.photosGrid}>
                      {selectedOrder.photos.map((photoUrl, index) => (
                        <a
                          key={`${photoUrl}-${index}`}
                          href={photoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.photoLink}
                        >
                          <img
                            src={photoUrl}
                            alt={`Foto ${index + 1}`}
                            className={styles.photoThumb}
                          />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.emptySection}>
                      No hay fotos registradas.
                    </p>
                  )}
                </div>

                <div className={styles.evidenceBlock}>
                  <label>Notas</label>
                  <p className={styles.notesText}>
                    {selectedOrder.observations?.trim() || "Sin notas"}
                  </p>
                </div>

                <div className={styles.evidenceBlock}>
                  <label>Firma</label>
                  {selectedOrder.clientSignature ? (
                    <a
                      href={selectedOrder.clientSignature}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.signatureLink}
                    >
                      <img
                        src={selectedOrder.clientSignature}
                        alt="Firma del cliente"
                        className={styles.signaturePreview}
                      />
                    </a>
                  ) : (
                    <p className={styles.emptySection}>Sin firma.</p>
                  )}
                </div>
              </section>
            </div>
          </div>
        )}
      </div>

      <WaterSampleFormModal
        isOpen={isWaterSampleModalOpen}
        onClose={() => setIsWaterSampleModalOpen(false)}
        clientId={selectedOrder ? Number(selectedOrder.client.id) : null}
        clientName={selectedOrder?.client.name}
        clientAddress={selectedOrder?.client.address}
        workOrderNumber={selectedOrder?.orderNumber}
        technicianId={
          currentUser?.id !== undefined && currentUser?.id !== null
            ? Number(currentUser.id)
            : null
        }
        existingForm={existingWaterSampleForm}
        onSaved={(saved) => setExistingWaterSampleForm(saved)}
      />
    </div>
  );
}
